import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';

function Avatar({ avatar, name, size = 40 }) {
  if (avatar) return <img className="avatar" src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} style={{ width: size, height: size }} onError={e => e.target.style.display = 'none'} />;
  return <div className="avatar-fallback" style={{ width: size, height: size, fontSize: size * 0.3 }}>{name?.slice(0,2).toUpperCase() || '??'}</div>;
}

const TIER_ORDER = ['Hall of Fame', 'Dynasty Elite', 'Powerhouse', 'Playoff Regular', 'Contender', 'Rookie Franchise'];

const TIER_STYLES = {
  'Hall of Fame':    { color: '#ffd32a', bg: 'rgba(255,211,42,0.09)',  border: 'rgba(255,211,42,0.35)', icon: '🏛️', desc: 'Immortalized league legacy — a franchise whose impact on this dynasty cannot be overstated.',                         hof_state_label: 'Inducted' },
  'Dynasty Elite':   { color: '#c084fc', bg: 'rgba(192,132,252,0.09)', border: 'rgba(192,132,252,0.32)', icon: '👑', desc: 'One of the league\'s defining teams — a franchise others have had to account for year after year.',                   hof_state_label: 'Dynasty Elite' },
  'Powerhouse':      { color: '#f97316', bg: 'rgba(249,115,22,0.09)',  border: 'rgba(249,115,22,0.32)',  icon: '💪', desc: 'A franchise others have to account for — consistent contention backed by a serious résumé.',                          hof_state_label: '' },
  'Playoff Regular': { color: '#22c55e', bg: 'rgba(34,197,94,0.09)',   border: 'rgba(34,197,94,0.30)',   icon: '✅', desc: 'Reliable, stable, and respected — a franchise that shows up when it matters.',                                       hof_state_label: '' },
  'Contender':       { color: '#38bdf8', bg: 'rgba(56,189,248,0.09)',  border: 'rgba(56,189,248,0.30)',  icon: '⚔️', desc: 'Dangerous but not yet established — a franchise knocking on the door of the upper tier.',                             hof_state_label: '' },
  'Rookie Franchise':{ color: '#6b7280', bg: 'rgba(107,114,128,0.08)', border: 'rgba(107,114,128,0.28)', icon: '🌱', desc: 'New, developing, and unproven — the beginning of what could become something special.',                              hof_state_label: '' },
};

const HOF_STATE_STYLES = {
  inducted:     { label: 'Inducted',      color: '#ffd32a', bg: 'rgba(255,211,42,0.12)',  icon: '🏛️' },
  eligible:     { label: 'HOF Eligible',  color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   icon: '⭐' },
  watchlist:    { label: 'HOF Watchlist', color: '#38bdf8', bg: 'rgba(56,189,248,0.10)',  icon: '👀' },
  not_eligible: { label: '',              color: '',         bg: '',                        icon: '' },
};

const WEIGHT_ICONS = {
  legendary: '🏆',
  major:     '⭐',
  minor:     '✅',
  milestone: '🛡️',
};

function ScoreBar({ score, maxScore }) {
  const pct = maxScore > 0 ? (score / maxScore) * 100 : 0;
  return (
    <div style={{ height: 4, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden', marginTop: 6 }}>
      <div style={{
        height: '100%', width: `${pct}%`,
        background: 'linear-gradient(90deg, var(--accent), var(--green))',
        borderRadius: 2, transition: 'width 0.8s ease',
      }} />
    </div>
  );
}

export default function LegacyBoard({ api }) {
  const { currentUser, csrfToken, refreshCsrf } = useUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [ballot, setBallot] = useState(null);
  const [ballotMessage, setBallotMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await fetch(`${api}/api/legacy-tiers`);
        if (r.ok) setData(await r.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [api]);

  const loadBallot = () => fetch(`${api}/api/hall-of-fame/dynasty/ballot`, { credentials: 'include' })
    .then(r => r.ok ? r.json() : null).then(setBallot).catch(() => {});
  useEffect(() => { loadBallot(); }, [api]);

  const castHallVote = async (candidateId, approve) => {
    setBallotMessage('Recording vote…');
    const token = csrfToken || await refreshCsrf();
    const response = await fetch(`${api}/api/hall-of-fame/dynasty/ballot/vote`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': token },
      body: JSON.stringify({ candidate_id: candidateId, approve }),
    });
    if (response.ok) { setBallotMessage('Vote recorded.'); await loadBallot(); }
    else setBallotMessage((await response.json().catch(() => ({}))).detail || 'Vote could not be recorded.');
  };

  if (loading) return (
    <div className="loading">
      <div className="spinner" />
      <span>Loading Legacy Board...</span>
    </div>
  );

  if (!data) return <div className="empty-state"><p>Legacy data unavailable.</p></div>;

  const board = data.legacy_board || [];
  const maxScore = board.length ? Math.max(...board.map(f => f.legacy_score)) : 1;
  const hallGroups = {
    inducted: board.filter(f => f.hof_state === 'inducted'),
    eligible: board.filter(f => f.hof_state === 'eligible'),
    watchlist: board.filter(f => f.hof_state === 'watchlist'),
  };

  const filtered = activeFilter === 'all'
    ? board
    : board.filter(f => f.tier === activeFilter);

  const tierCounts = {};
  board.forEach(f => { tierCounts[f.tier] = (tierCounts[f.tier] || 0) + 1; });

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Legacy Board</div>
        <div className="page-subtitle">
          Franchise prestige rankings · Powered by historical achievements · Legacy Score is sticky and hard to erase
        </div>
      </div>

      {/* Philosophy banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(var(--accent2-rgb),0.06), rgba(var(--accent-rgb),0.04))',
        border: '1px solid rgba(255,211,42,0.2)',
        borderRadius: 12, padding: '16px 20px', marginBottom: 20,
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, #ffd32a, transparent)' }} />
        <div style={{ fontSize: '0.72rem', color: 'rgba(255,211,42,0.8)', letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 6 }}>
          Legacy Philosophy
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text2)', lineHeight: 1.6 }}>
          Legacy Score is <strong style={{ color: 'var(--text)' }}>additive only</strong> — achievements are never removed and bad seasons add nothing negative.
          A franchise that won back-to-back championships cannot be demoted by one poor year.
          Prestige is earned through history, not erased by recency.
        </div>
      </div>

      {/* Canonical Hall of Fame */}
      <section className="card" style={{ marginBottom: 20, borderColor: 'rgba(255,211,42,0.24)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ color: '#ffd32a', fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: 2, textTransform: 'uppercase' }}>Dynasty Hall of Fame</div>
            <div style={{ color: 'var(--text)', fontFamily: 'var(--font-display)', fontSize: '1.45rem', marginTop: 4 }}>Legacy must be accomplished, not projected.</div>
          </div>
          <div style={{ color: 'var(--text3)', fontSize: '0.67rem', fontFamily: 'var(--font-mono)', maxWidth: 360, lineHeight: 1.5 }}>
            Three titles guarantees a place on the ballot. Every induction still requires ⅔ approval and 70% quorum.
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 8 }}>
          {[
            ['inducted', '🏛️ Inducted', '#ffd32a'],
            ['eligible', '⭐ Ballot Eligible', '#22c55e'],
            ['watchlist', '👀 Watchlist', '#38bdf8'],
          ].map(([state, label, color]) => (
            <div key={state} style={{ border: `1px solid ${color}33`, borderRadius: 9, padding: '12px 13px', background: `${color}0b` }}>
              <div style={{ color, fontSize: '0.64rem', fontFamily: 'var(--font-mono)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 7 }}>{label} · {hallGroups[state].length}</div>
              {hallGroups[state].length ? hallGroups[state].map(manager => (
                <div key={manager.franchise_id} style={{ color: 'var(--text)', fontSize: '0.78rem', marginTop: 5 }}>
                  {manager.display_name}
                  <span style={{ color: 'var(--text3)', marginLeft: 5 }}>· {manager.championships} titles · {manager.finals_appearances} finals</span>
                  {state === 'eligible' && ballot?.status === 'open' && currentUser?.display_name !== manager.display_name && (
                    <span style={{ display: 'flex', gap: 5, marginTop: 6 }}>
                      {[true, false].map(approve => (
                        <button key={String(approve)} onClick={() => castHallVote(manager.franchise_id, approve)} style={{
                          border: `1px solid ${ballot.my_votes?.[manager.franchise_id] === approve ? (approve ? '#22c55e' : '#ef4444') : 'var(--border)'}`,
                          background: 'var(--bg3)', color: 'var(--text2)', borderRadius: 5, padding: '4px 8px', cursor: 'pointer', fontSize: '0.62rem', fontFamily: 'var(--font-mono)'
                        }}>{approve ? 'Approve' : 'Decline'}</button>
                      ))}
                    </span>
                  )}
                  {state === 'eligible' && ballot?.status === 'open' && currentUser?.display_name === manager.display_name && (
                    <div style={{ color: 'var(--text3)', fontSize: '0.62rem', marginTop: 5 }}>You cannot vote on your own candidacy.</div>
                  )}
                </div>
              )) : <div style={{ color: 'var(--text3)', fontSize: '0.72rem' }}>No qualifying franchises</div>}
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--border)', color: 'var(--text3)', fontSize: '0.68rem', lineHeight: 1.55 }}>
          Only completed-season titles, finals, playoffs, longevity and all-time ranks count. Roster value, projections, trade grades and Legacy Score do not create Hall eligibility. The annual ballot opens after the season audit and seven-day correction window.
          {ballot?.status === 'open' && <strong style={{ color: '#ffd32a', marginLeft: 5 }}>Class of {ballot.class_year} voting is open.</strong>}
          {ballotMessage && <span style={{ marginLeft: 7 }}>{ballotMessage}</span>}
        </div>
      </section>

      {/* Tier summary */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveFilter('all')}
          style={{
            background: activeFilter === 'all' ? 'rgba(var(--accent-rgb),0.15)' : 'var(--bg3)',
            border: `1px solid ${activeFilter === 'all' ? 'rgba(var(--accent-rgb),0.4)' : 'var(--border)'}`,
            borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
            color: activeFilter === 'all' ? 'var(--accent)' : 'var(--text2)',
            fontSize: '0.78rem', fontFamily: 'var(--font-mono)',
          }}
        >
          All ({board.length})
        </button>
        {TIER_ORDER.filter(t => tierCounts[t]).map(tier => {
          const style = TIER_STYLES[tier];
          const isActive = activeFilter === tier;
          return (
            <button
              key={tier}
              onClick={() => setActiveFilter(isActive ? 'all' : tier)}
              style={{
                background: isActive ? style.bg : 'var(--bg3)',
                border: `1px solid ${isActive ? style.border : 'var(--border)'}`,
                borderRadius: 8, padding: '8px 14px', cursor: 'pointer',
                color: isActive ? style.color : 'var(--text2)',
                fontSize: '0.78rem', fontFamily: 'var(--font-mono)',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>{style.icon}</span>
              <span>{tier}</span>
              <span style={{ opacity: 0.6 }}>({tierCounts[tier]})</span>
            </button>
          );
        })}
      </div>

      {/* Board */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map((franchise, i) => {
          const tier = TIER_STYLES[franchise.tier] || TIER_STYLES.Developing;
          const isExpanded = expanded === franchise.franchise_id;
          const achievements = typeof franchise.achievements === 'string'
            ? JSON.parse(franchise.achievements)
            : franchise.achievements || [];

          return (
            <div
              key={franchise.franchise_id}
              onClick={() => setExpanded(isExpanded ? null : franchise.franchise_id)}
              style={{
                background: tier.bg,
                border: `1px solid ${isExpanded ? tier.border : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 12, padding: '16px 18px',
                cursor: 'pointer', position: 'relative', overflow: 'hidden',
                transition: 'border-color 0.2s',
              }}
            >
              {/* Tier accent line */}
              {['Hall of Fame', 'Dynasty Elite'].includes(franchise.tier) && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${tier.color}, transparent)` }} />
              )}

              {/* Main row */}
              <div style={{ display: 'grid', gridTemplateColumns: '44px 44px 1fr auto', gap: 12, alignItems: 'center' }}>
                {/* Rank */}
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: '1.8rem', lineHeight: 1,
                    color: franchise.rank === 1 ? tier.color : 'var(--text2)',
                  }}>
                    {franchise.rank}
                  </div>
                </div>

                <Avatar avatar={franchise.avatar} name={franchise.display_name} size={44} />

                {/* Info */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{franchise.display_name}</span>
                    <span style={{
                      fontSize: '0.65rem', padding: '2px 8px', borderRadius: 10,
                      background: tier.bg, color: tier.color,
                      border: `1px solid ${tier.border}`,
                      fontFamily: 'var(--font-mono)', fontWeight: 700,
                    }}>
                      {tier.icon} {franchise.tier}
                    </span>
                    {franchise.championships > 0 && (
                      <span style={{ fontSize: '0.82rem' }}>{'🏆'.repeat(franchise.championships)}</span>
                    )}
                    {franchise.hof_state && franchise.hof_state !== 'not_eligible' && (() => {
                      const hs = HOF_STATE_STYLES[franchise.hof_state];
                      return (
                        <span style={{ fontSize: '0.65rem', padding: '2px 8px', borderRadius: 10,
                          background: hs.bg, color: hs.color, border: `1px solid ${hs.color}44`,
                          fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                          {hs.icon} {hs.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <span>{franchise.all_time_wins}-{franchise.all_time_losses}</span>
                    <span>·</span>
                    <span>{(franchise.all_time_win_pct * 100).toFixed(1)}% win</span>
                    <span>·</span>
                    <span>{franchise.seasons} seasons</span>
                    <span>·</span>
                    <span style={{ color: 'var(--text2)' }}>{franchise.total_achievements} achievements</span>
                  </div>
                  <ScoreBar score={franchise.legacy_score} maxScore={maxScore} />
                </div>

                {/* Score */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontFamily: 'var(--font-display)', fontSize: '1.8rem',
                    color: tier.color, lineHeight: 1,
                  }}>
                    {franchise.legacy_score}
                  </div>
                  <div style={{ fontSize: '0.58rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Legacy
                  </div>
                </div>
              </div>

              {/* Achievement count pills */}
              <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
                {franchise.legendary_count > 0 && (
                  <span style={{ fontSize: '0.65rem', color: '#ffd32a', background: 'rgba(255,211,42,0.1)', border: '1px solid rgba(255,211,42,0.2)', padding: '2px 8px', borderRadius: 6, fontFamily: 'var(--font-mono)' }}>
                    🏆 {franchise.legendary_count} Legendary
                  </span>
                )}
                {franchise.major_count > 0 && (
                  <span style={{ fontSize: '0.65rem', color: 'var(--accent)', background: 'rgba(var(--accent-rgb),0.1)', border: '1px solid rgba(var(--accent-rgb),0.2)', padding: '2px 8px', borderRadius: 6, fontFamily: 'var(--font-mono)' }}>
                    ⭐ {franchise.major_count} Major
                  </span>
                )}
                {franchise.milestone_count > 0 && (
                  <span style={{ fontSize: '0.65rem', color: '#a855f7', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.2)', padding: '2px 8px', borderRadius: 6, fontFamily: 'var(--font-mono)' }}>
                    🛡️ {franchise.milestone_count} Milestone
                  </span>
                )}
              </div>

              {/* Expanded section */}
              {isExpanded && (
                <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${tier.border}` }}>

                  {/* Profile Card */}
                  {franchise.profile_card && (
                    <div style={{ marginBottom: 16, padding: '12px 14px', background: 'rgba(0,0,0,0.18)', borderRadius: 10, border: `1px solid ${tier.border}` }}>
                      <div style={{ fontSize: '0.65rem', color: tier.color, textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>
                        Legacy Profile
                      </div>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                        {/* Current Legacy Score */}
                        <div>
                          <div style={{ fontSize: '0.58rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>Current Legacy Score</div>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: tier.color, lineHeight: 1 }}>{franchise.profile_card.current_score}</div>
                        </div>
                        {/* Locked Status */}
                        <div>
                          <div style={{ fontSize: '0.58rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Locked Status</div>
                          <span style={{
                            fontSize: '0.65rem', padding: '3px 10px', borderRadius: 8,
                            background: (TIER_STYLES[franchise.profile_card.locked_status_id] || TIER_STYLES['Rookie Franchise']).bg,
                            color: franchise.profile_card.locked_status_color,
                            border: `1px solid ${(TIER_STYLES[franchise.profile_card.locked_status_id] || TIER_STYLES['Rookie Franchise']).border}`,
                            fontFamily: 'var(--font-mono)', fontWeight: 700,
                          }}>
                            {franchise.profile_card.locked_status_icon} {franchise.profile_card.locked_status_label}
                          </span>
                          {franchise.profile_card.locked_status_id !== franchise.profile_card.current_tier && (
                            <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
                              Locked at {franchise.profile_card.locked_status_label} — current score reflects {franchise.profile_card.current_tier}
                            </div>
                          )}
                        </div>
                        {/* HOF Eligibility */}
                        {franchise.profile_card.hof_state && franchise.profile_card.hof_state !== 'not_eligible' && (() => {
                          const hs = HOF_STATE_STYLES[franchise.profile_card.hof_state];
                          return (
                            <div>
                              <div style={{ fontSize: '0.58rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>HOF Eligibility</div>
                              <span style={{ fontSize: '0.65rem', padding: '3px 10px', borderRadius: 8,
                                background: hs.bg, color: hs.color, border: `1px solid ${hs.color}44`,
                                fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                                {hs.icon} {hs.label}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      {/* Next Tier Progress */}
                      {franchise.profile_card.next_tier && (
                        <div>
                          <div style={{ fontSize: '0.58rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Next Tier Progress</div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text2)', marginBottom: 4, fontFamily: 'var(--font-mono)' }}>
                            {franchise.profile_card.next_tier.pts_needed} pts to {franchise.profile_card.next_tier.icon} {franchise.profile_card.next_tier.label}
                          </div>
                          <div style={{ height: 6, background: 'var(--bg4)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{
                              height: '100%',
                              width: `${franchise.profile_card.next_tier.progress_pct}%`,
                              background: `linear-gradient(90deg, ${tier.color}, ${tier.color}88)`,
                              borderRadius: 3,
                              transition: 'width 0.8s ease',
                            }} />
                          </div>
                        </div>
                      )}
                      {franchise.hof_case?.why?.length > 0 && (
                        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${tier.border}` }}>
                          <div style={{ fontSize: '0.58rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 5 }}>Hall résumé</div>
                          {franchise.hof_case.why.map(reason => (
                            <div key={reason} style={{ color: 'var(--text2)', fontSize: '0.7rem', marginTop: 4 }}>✓ {reason}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Achievement Breakdown */}
                  <div style={{ fontSize: '0.65rem', color: tier.color, textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>
                    Achievement Breakdown
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {achievements.map((a, j) => (
                      <div key={j} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px',
                        background: 'rgba(0,0,0,0.2)', borderRadius: 8,
                      }}>
                        <span style={{ fontSize: '1rem', flexShrink: 0 }}>{WEIGHT_ICONS[a.weight_class] || '🎖️'}</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text)' }}>{a.label}</div>
                          {a.season && (
                            <div style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{a.season} Season</div>
                          )}
                        </div>
                        {a.legacy_score_modifier > 0 && (
                          <span style={{
                            fontSize: '0.72rem', color: tier.color,
                            fontFamily: 'var(--font-mono)', fontWeight: 700,
                            background: tier.bg, padding: '2px 8px', borderRadius: 4,
                          }}>
                            +{a.legacy_score_modifier}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ textAlign: 'center', marginTop: 8, fontSize: '0.62rem', color: 'var(--text3)' }}>
                {isExpanded ? '▲ collapse' : '▼ expand achievements'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tier legend */}
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ fontSize: '0.68rem', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12, fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
          Tier Definitions
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(data.tiers || []).map(tierDef => {
            const style = TIER_STYLES[tierDef.id] || TIER_STYLES['Rookie Franchise'];
            const isHof = tierDef.id === 'Hall of Fame';
            return (
              <div key={tierDef.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <span style={{
                  fontSize: '0.7rem', padding: '3px 10px', borderRadius: 8, flexShrink: 0,
                  background: style.bg, color: style.color, border: `1px solid ${style.border}`,
                  fontFamily: 'var(--font-mono)', fontWeight: 700, minWidth: 110, textAlign: 'center',
                }}>
                  {tierDef.icon} {tierDef.label}
                </span>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text2)' }}>{tierDef.desc}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
                    {isHof ? 'League vote required · maximum one inductee per season' : `${tierDef.min_score}+ Legacy Score`}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
